import { Modal, Progress } from 'antd';
import { Fragment, PureComponent, ReactNode } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { ThunkDispatch } from 'redux-thunk';
import { GO_TO_MY_GENIE } from '../routes/paths';
import { Actions } from '../store/actions';
import { fetchMyGenie } from '../store/actions/login';
import { answerSnackingQuestion, fetchSnackingQuestions } from '../store/actions/session';
import { DB_TABLES } from '../store/dbTables';
import { getDoc, getG, getGArray } from '../store/idbHandler';
import { GlobalState } from '../store/reducers';
import { MedicineCategory, MedicationsEntity } from '../store/types/prescription';
import { track } from '../tracking';
import ShowDrug from './ShowDrug';
import { captureError } from './captureError';
import { DoctorProfile, SnackingQuestion } from '../api/getSnackingQuestions';
import { GMedication } from '../store/db';
import { LOCAL_ID_PREFIX, MAX_G_MEDICATIONS } from '../constants';
import SnackingQuestionsModal from './SnackingQuestionsModal';
import { getMyDbId } from '../api/mactiveid';

interface Props extends RouteComponentProps {
    dispatch: ThunkDispatch<GlobalState, null, Actions>;
}

type State = {
    isModalVisible: boolean;
    isNewModalVisible: boolean;
    snackingQuestions: SnackingQuestion[];
    doctorProfile?: DoctorProfile;
    activeIndex: number;
    isUpdateGenieLoading?: boolean;
    totalUncodedDrugs?: number;
    isFetchingQuestions?: boolean;
    isViewAllLoading?: boolean;
};

export const NOTA = 'nota';
export const SNACKING_DOCTOR_PROFILE = 'snackingDoctorProfile';
export const SNACKING_QUESTIONS_VERSION = 'snackingQuestionsVersion';
export const SNACKING_QUESTIONS_FETCH_DATE = 'snackingQuestionsFetchDate';
export const PARCHI_SNACKING_QUESTIONS_VERSION = 'parchiSnackingQuestionsVersion';
export const SNACKING_POPUP_SHOWN_DATE = 'snackingPopupShownDate';
export const SNACKING_POPUP_MESSAGE_SHOWN_DATE = 'snackingPopupMessageShownDate';

class SnackingQuestionsComponent extends PureComponent<Props, State> {
    state: State = {
        isModalVisible: false,
        isNewModalVisible: false,
        snackingQuestions: [],
        activeIndex: 0,
    };

    componentDidMount = async (): Promise<void> => {
        this.onMount();
    };

    onMount = async (): Promise<void> => {
        const { dispatch } = this.props;
        const gMedications = await getG<GMedication>(
            getMyDbId() || '',
            DB_TABLES['medications'],
            MAX_G_MEDICATIONS,
        );
        const uncodedMedications = gMedications.filter((i) => i.id.startsWith(LOCAL_ID_PREFIX));
        const today = new Date().toISOString().slice(0, 10);
        const snackingQuestionsFetchDate = localStorage.getItem(SNACKING_QUESTIONS_FETCH_DATE);

        try {
            const snackingQuestions: SnackingQuestion[] = await getGArray<SnackingQuestion>(
                getMyDbId() || '',
                DB_TABLES['snackingQuestions'],
            );
            if (today !== snackingQuestionsFetchDate) {
                dispatch(fetchSnackingQuestions());
            } else {
                this.setState({
                    snackingQuestions,
                    totalUncodedDrugs: uncodedMedications.length,
                    activeIndex: 0,
                });
                track('SNACKING_QUESTIONS', {
                    action: 'MOUNT',
                    questions: String(snackingQuestions.length),
                });
            }
        } catch (err) {
            console.error(err);
            captureError(err as Error);
        }
    };

    showModalHandler = () => {
        this.setState({
            isModalVisible: true,
        });
        const today = new Date().toISOString().slice(0, 10);
        localStorage.setItem(SNACKING_POPUP_SHOWN_DATE, today);
        track('SNACKING_QUESTIONS', { action: 'OPEN' });
    };

    hideModalHandler = () => {
        const { dispatch } = this.props;
        const docId = getDoc()?._id;
        this.setState({
            isModalVisible: false,
        });
        docId && dispatch(fetchMyGenie());
        track('SNACKING_QUESTIONS', { action: 'CLOSE' });
    };

    onSelect = (drug: MedicationsEntity): void => {
        const { dispatch } = this.props;
        const { snackingQuestions, activeIndex } = this.state;
        const { setIsUpdateGenieLoading, setIsFetchingQuestions } = this;
        const { q_id } = snackingQuestions[activeIndex];
        const isLast = activeIndex + 1 === snackingQuestions.length;

        dispatch(
            answerSnackingQuestion(
                drug,
                q_id,
                setIsUpdateGenieLoading,
                setIsFetchingQuestions,
                isLast,
            ),
        );

        this.setState({
            activeIndex: activeIndex + 1,
            isUpdateGenieLoading: isLast,
            isFetchingQuestions: isLast,
        });
    };

    onOtherOptionSelect = (is_otc?: boolean, med_cat?: MedicineCategory): void => {
        const { dispatch } = this.props;
        const { snackingQuestions, activeIndex } = this.state;
        const { setIsUpdateGenieLoading, setIsFetchingQuestions } = this;
        const { med, suggestions, q_id } = snackingQuestions[activeIndex];
        const isLast = activeIndex + 1 === snackingQuestions.length;

        dispatch(
            answerSnackingQuestion(
                { ...med, is_otc, med_cat, suggestions },
                q_id,
                setIsUpdateGenieLoading,
                setIsFetchingQuestions,
                isLast,
            ),
        );

        this.setState({
            activeIndex: activeIndex + 1,
            isUpdateGenieLoading: isLast,
            isFetchingQuestions: isLast,
        });
    };

    goToGenieDashboardHandler = () => {
        const { history } = this.props;
        history.push(GO_TO_MY_GENIE('genie-configuration', 'medicines', 'uncoded'));
        track('SNACKING_QUESTIONS', { action: 'VIEW_ALL' });
    };

    onSkip = () => {
        const { dispatch } = this.props;
        const { snackingQuestions, activeIndex } = this.state;
        const { setIsUpdateGenieLoading, setIsFetchingQuestions } = this;
        const { med, q_id } = snackingQuestions[activeIndex];
        const isLast = activeIndex + 1 === snackingQuestions.length;

        dispatch(
            answerSnackingQuestion(
                med,
                q_id,
                setIsUpdateGenieLoading,
                setIsFetchingQuestions,
                isLast,
                true,
            ),
        );
        this.setState({
            activeIndex: activeIndex + 1,
            isUpdateGenieLoading: isLast,
            isFetchingQuestions: isLast,
        });
    };

    setIsUpdateGenieLoading = () => {
        this.setState({
            isUpdateGenieLoading: false,
        });
    };

    setIsFetchingQuestions = () => {
        this.setState({
            isFetchingQuestions: false,
        });
    };

    onGoToGenieDashboardHandler = () => {
        const { isUpdateGenieLoading } = this.state;
        if (!isUpdateGenieLoading) this.goToGenieDashboardHandler();
    };

    viewAllHandler = () => {
        const { dispatch } = this.props;
        const { isViewAllLoading } = this.state;
        if (isViewAllLoading) return;
        this.setState({ isViewAllLoading: true });
        const docId = getDoc()?._id;
        docId && dispatch(fetchMyGenie(undefined, undefined, this.goToGenieDashboardHandler));
    };

    newModalToggleHandler = () => {
        const { isNewModalVisible } = this.state;
        this.setState({
            isNewModalVisible: !isNewModalVisible,
        });
        const today = new Date().toISOString().slice(0, 10);
        localStorage.setItem(SNACKING_POPUP_MESSAGE_SHOWN_DATE, today);
        track('SNACKING_QUESTIONS_NEW', { action: isNewModalVisible ? 'CLOSE' : 'OPEN' });
    };

    onSuccess = () => {
        const { snackingQuestions } = this.state;
        this.setState({
            activeIndex: snackingQuestions.length,
        });
    };

    fetchNextQuestions = () => {
        this.onMount();
        localStorage.removeItem(SNACKING_DOCTOR_PROFILE);
    };

    render(): ReactNode {
        const {
            isModalVisible,
            snackingQuestions,
            activeIndex,
            isUpdateGenieLoading,
            totalUncodedDrugs,
            isFetchingQuestions,
            isNewModalVisible,
        } = this.state;
        const {
            showModalHandler,
            hideModalHandler,
            onSelect,
            onSkip,
            onOtherOptionSelect,
            goToGenieDashboardHandler,
            viewAllHandler,
            newModalToggleHandler,
            onSuccess,
            fetchNextQuestions,
        } = this;
        const currentQuestion = snackingQuestions[activeIndex];
        const today = new Date().toISOString().slice(0, 10);
        const snackingPopupShownDate = localStorage.getItem(SNACKING_POPUP_SHOWN_DATE);
        const snackingPopupMessageShownDate = localStorage.getItem(
            SNACKING_POPUP_MESSAGE_SHOWN_DATE,
        );
        const doctorProfile = JSON.parse(localStorage.getItem(SNACKING_DOCTOR_PROFILE) as string);

        return (
            <Fragment>
                {activeIndex !== snackingQuestions.length &&
                    (doctorProfile ? (
                        <div className="w-360 absolute right-16 bottom-0 select-none z-50">
                            {today !== snackingPopupMessageShownDate && (
                                <div
                                    className="popup-overlay-show cursor-pointer"
                                    onClick={newModalToggleHandler}
                                >
                                    <div className="p-16 flex items-start space-x-10 bg-darwin-accent-bright-blue-800 rounded-4 text-white">
                                        <div className="relative bg-white rounded-full">
                                            <img
                                                src={doctorProfile.img}
                                                className="w-176 h-48 rounded-full pt-2"
                                            />
                                            <p className="flex items-center justify-center absolute -top-6 -right-4 text-12 bg-darwin-status-warning-500 text-white w-22 h-22 rounded-full font-700">
                                                1
                                            </p>
                                        </div>
                                        <p>
                                            {doctorProfile.name} has suggested changes to your drug
                                            database to ensure compliance with NMC Guidelines.
                                            Please review the changes and provide confirmation
                                        </p>
                                    </div>

                                    <div className="flex justify-center">
                                        <svg
                                            className="w-16 h-12 fill-darwin-accent-bright-blue-800"
                                            viewBox="0 0 16 12"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path d="M8 12L16 0H0L8 12Z" />
                                        </svg>
                                    </div>
                                </div>
                            )}

                            <div
                                className="px-16 py-12 bg-darwin-neutral-900 rounded-t-8 cursor-pointer mt-8"
                                onClick={newModalToggleHandler}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-8 font-700 text-white">
                                        <svg
                                            className="w-16 h-16 fill-white"
                                            viewBox="0 0 16 16"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path d="M8 16C3.5625 16 0 12.4375 0 8C0 3.59375 3.5625 0 8 0C12.4062 0 16 3.59375 16 8C16 12.4375 12.4062 16 8 16ZM6.75 10.5C6.3125 10.5 6 10.8438 6 11.25C6 11.6875 6.3125 12 6.75 12H9.25C9.65625 12 10 11.6875 10 11.25C10 10.8438 9.65625 10.5 9.25 10.5H9V7.75C9 7.34375 8.65625 7 8.25 7H6.75C6.3125 7 6 7.34375 6 7.75C6 8.1875 6.3125 8.5 6.75 8.5H7.5V10.5H6.75ZM8 6C8.53125 6 9 5.5625 9 5C9 4.46875 8.53125 4 8 4C7.4375 4 7 4.46875 7 5C7 5.5625 7.4375 6 8 6Z" />
                                        </svg>

                                        <p>Message from Eka Medico-legal team</p>
                                    </div>

                                    <div>
                                        <svg
                                            className="w-16 h-16 fill-white"
                                            viewBox="0 0 16 10"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path d="M7.28125 1.3125C7.65625 0.90625 8.3125 0.90625 8.6875 1.3125L14.6875 7.3125C15.0938 7.6875 15.0938 8.34375 14.6875 8.71875C14.3125 9.125 13.6562 9.125 13.2812 8.71875L8 3.4375L2.6875 8.71875C2.3125 9.125 1.65625 9.125 1.28125 8.71875C0.875 8.34375 0.875 7.6875 1.28125 7.3125L7.28125 1.3125Z" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div
                            className="space-y-12 px-16 py-12 bg-darwin-neutral-900 rounded-t-8 w-360 absolute right-16 bottom-0 cursor-pointer select-none"
                            onClick={showModalHandler}
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-8 font-700 text-white">
                                    <svg
                                        className="w-16 h-16 fill-white"
                                        viewBox="0 0 18 14"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path d="M9 0C9.4375 0 9.84375 0.25 10.0625 0.625L16.8125 12.125C17.0312 12.5312 17.0312 13 16.8125 13.375C16.5938 13.7812 16.1875 14 15.75 14H2.25C1.78125 14 1.375 13.7812 1.15625 13.375C0.9375 13 0.9375 12.5312 1.15625 12.125L7.90625 0.625C8.125 0.25 8.53125 0 9 0ZM9 4C8.5625 4 8.25 4.34375 8.25 4.75V8.25C8.25 8.6875 8.5625 9 9 9C9.40625 9 9.75 8.6875 9.75 8.25V4.75C9.75 4.34375 9.40625 4 9 4ZM10 11C10 10.4688 9.53125 10 9 10C8.4375 10 8 10.4688 8 11C8 11.5625 8.4375 12 9 12C9.53125 12 10 11.5625 10 11Z" />
                                    </svg>

                                    <p>Drugs with Potential Medico-Legal Risk</p>

                                    <p className="flex items-center justify-center w-24 h-24 text-14 bg-darwin-status-warning-500 rounded-full">
                                        {snackingQuestions.length - activeIndex}
                                    </p>
                                </div>

                                <div>
                                    <svg
                                        className="w-16 h-16 fill-white"
                                        viewBox="0 0 16 10"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path d="M7.28125 1.3125C7.65625 0.90625 8.3125 0.90625 8.6875 1.3125L14.6875 7.3125C15.0938 7.6875 15.0938 8.34375 14.6875 8.71875C14.3125 9.125 13.6562 9.125 13.2812 8.71875L8 3.4375L2.6875 8.71875C2.3125 9.125 1.65625 9.125 1.28125 8.71875C0.875 8.34375 0.875 7.6875 1.28125 7.3125L7.28125 1.3125Z" />
                                    </svg>
                                </div>
                            </div>

                            {today !== snackingPopupShownDate && (
                                <p className="text-darwin-neutral-900 bg-white px-12 py-8 rounded-8">
                                    You prescribed{' '}
                                    <span className="font-700">
                                        a few medications with potential dispensing errors
                                    </span>{' '}
                                    in your last prescriptions.
                                </p>
                            )}
                        </div>
                    ))}

                <Modal
                    title={
                        <div className="flex items-center justify-between pr-40">
                            <p className="font-700">Drugs with Potential Medico-Legal Risk</p>

                            <div
                                className="w-120 py-4 px-8 flex items-center space-x-8 text-white rounded-4"
                                style={{
                                    background:
                                        'linear-gradient(125.33deg, #B82014 6.05%, #7A0A16 103.14%)',
                                }}
                            >
                                <p className="font-700 text-18">{totalUncodedDrugs}</p>
                                <p className="text-11">Total drugs with issues</p>
                            </div>
                        </div>
                    }
                    visible={isModalVisible}
                    onOk={hideModalHandler}
                    onCancel={hideModalHandler}
                    destroyOnClose
                    width={800}
                    okText="Done"
                    bodyStyle={{
                        padding: '0px',
                    }}
                    className="space-y-16"
                    cancelButtonProps={{ style: { display: 'none' } }}
                    closeIcon={
                        <div className="flex items-center justify-center h-full">
                            <svg
                                className="w-14 h-14 fill-text-secondary relative top-8"
                                viewBox="0 0 12 12"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path d="M10.75 9.71875C11.0312 10.0312 11.0312 10.5 10.75 10.7812C10.4375 11.0938 9.96875 11.0938 9.6875 10.7812L6 7.0625L2.28125 10.7812C1.96875 11.0938 1.5 11.0938 1.21875 10.7812C0.90625 10.5 0.90625 10.0312 1.21875 9.71875L4.9375 6L1.21875 2.28125C0.90625 1.96875 0.90625 1.5 1.21875 1.21875C1.5 0.90625 1.96875 0.90625 2.25 1.21875L6 4.96875L9.71875 1.25C10 0.9375 10.4688 0.9375 10.75 1.25C11.0625 1.53125 11.0625 2 10.75 2.3125L7.03125 6L10.75 9.71875Z" />
                            </svg>
                        </div>
                    }
                    footer={
                        <div className="flex items-center justify-between">
                            <button
                                className="text-darwin-primary-500 font-700"
                                onClick={viewAllHandler}
                                disabled={isUpdateGenieLoading}
                            >
                                View all
                            </button>

                            <button
                                className="px-16 py-8 bg-darwin-primary-500 text-white rounded-4 font-700"
                                onClick={hideModalHandler}
                            >
                                Done
                            </button>
                        </div>
                    }
                >
                    <div className="flex items-center space-x-12 px-16 pt-8 pb-4">
                        <p className="text-darwin-neutral-900 font-700">Needs attention today:</p>

                        <div className="flex-1">
                            <Progress
                                percent={Math.round((activeIndex * 100) / snackingQuestions.length)}
                            />
                        </div>
                    </div>

                    {currentQuestion ? (
                        <div
                            key={currentQuestion.q_id}
                            className="border border border-darwin-neutral-200 rounded-8 overflow-hidden space-y-16 m-16 popup-overlay-show mt-12"
                        >
                            <ShowDrug
                                drug={currentQuestion.med}
                                matchingDrugs={currentQuestion.suggestions}
                                onChange={onSelect}
                                onSkip={onSkip}
                                onOtherOptionSelect={onOtherOptionSelect}
                                fCount={currentQuestion.fCount}
                            />
                        </div>
                    ) : (
                        <div className="space-y-24 p-24">
                            <p className="text-15 text-center">
                                👏 Wow! You are one step closer to successfully safe-guarding your
                                practice from Medico-Legal Risk.
                            </p>

                            <div className="flex items-center justify-center space-x-20">
                                <button
                                    className="flex items-center space-x-8 px-16 py-8 border border-darwin-primary-500 rounded-4"
                                    onClick={goToGenieDashboardHandler}
                                    disabled={isUpdateGenieLoading}
                                >
                                    <span className="text-darwin-primary-500 font-700">
                                        Fix all {totalUncodedDrugs} issues
                                    </span>
                                    {isUpdateGenieLoading ? (
                                        <svg
                                            className="w-14 h-16 fill-darwin-primary-500 animate-spin"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 512 512"
                                        >
                                            <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                                        </svg>
                                    ) : (
                                        <svg
                                            className="w-14 h-16 fill-darwin-primary-500"
                                            viewBox="0 0 14 14"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path d="M8.75 1.5C8.3125 1.5 8 1.1875 8 0.75C8 0.34375 8.3125 0 8.75 0H13.25C13.6562 0 14 0.34375 14 0.75V5.25C14 5.6875 13.6562 6 13.25 6C12.8125 6 12.5 5.6875 12.5 5.25V2.5625L6.25 8.78125C5.96875 9.09375 5.5 9.09375 5.21875 8.78125C4.90625 8.5 4.90625 8.03125 5.21875 7.71875L11.4375 1.5H8.75ZM0 2.75C0 1.8125 0.78125 1 1.75 1H5.25C5.65625 1 6 1.34375 6 1.75C6 2.1875 5.65625 2.5 5.25 2.5H1.75C1.59375 2.5 1.5 2.625 1.5 2.75V12.25C1.5 12.4062 1.59375 12.5 1.75 12.5H11.25C11.375 12.5 11.5 12.4062 11.5 12.25V8.75C11.5 8.34375 11.8125 8 12.25 8C12.6562 8 13 8.34375 13 8.75V12.25C13 13.2188 12.1875 14 11.25 14H1.75C0.78125 14 0 13.2188 0 12.25V2.75Z" />
                                        </svg>
                                    )}
                                </button>

                                <button
                                    className="flex items-center space-x-8 px-16 py-8 bg-darwin-primary-500 font-700 text-white rounded-4"
                                    onClick={fetchNextQuestions}
                                    disabled={isFetchingQuestions}
                                >
                                    {isFetchingQuestions && (
                                        <svg
                                            className="w-16 h-16 fill-white animate-spin"
                                            role="img"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 512 512"
                                        >
                                            <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                                        </svg>
                                    )}

                                    <span>Fix next 10 issues</span>
                                </button>
                            </div>
                        </div>
                    )}
                </Modal>

                <SnackingQuestionsModal
                    isModalVisible={isNewModalVisible}
                    hideModalHandler={newModalToggleHandler}
                    snackingQuestion={snackingQuestions}
                    doctorProfile={doctorProfile}
                    onSuccess={onSuccess}
                />
            </Fragment>
        );
    }
}
export default connect()(withRouter(SnackingQuestionsComponent));
